import { createForOfIteratorHelper as _createForOfIteratorHelper, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, typeof as _typeof } from '../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { cn } from '@costar-ui/cosmos';
import { useObjectRef, mergeRefs, mergeProps, useLayoutEffect } from '@react-aria/utils';
import React__default, { useMemo, useContext, useState, useRef, useCallback, createContext } from 'react';
import { useIsSSR } from 'react-aria';
import ReactDOM from 'react-dom';

var _excluded = ["ref"];

// Override forwardRef types so generics work.

var DEFAULT_SLOT = Symbol('default');
function Provider(_ref) {
  var values = _ref.values,
    children = _ref.children;
  var _iterator = _createForOfIteratorHelper(values),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _step$value = _slicedToArray(_step.value, 2),
        Context = _step$value[0],
        value = _step$value[1];
      // @ts-ignore
      children = /*#__PURE__*/React__default.createElement(Context.Provider, {
        value: value
      }, children);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return children;
}
function useRenderProps(props) {
  var className = props.className,
    style = props.style,
    children = props.children,
    _props$defaultClassNa = props.defaultClassName,
    defaultClassName = _props$defaultClassNa === void 0 ? undefined : _props$defaultClassNa,
    _props$defaultChildre = props.defaultChildren,
    defaultChildren = _props$defaultChildre === void 0 ? undefined : _props$defaultChildre,
    defaultStyle = props.defaultStyle,
    values = props.values;
  return useMemo(function () {
    var _computedClassName, _computedChildren;
    var computedClassName;
    var computedStyle;
    var computedChildren;
    if (typeof className === 'function') {
      computedClassName = className(_objectSpread2(_objectSpread2({}, values), {}, {
        defaultClassName: defaultClassName
      }));
    } else {
      computedClassName = className;
    }
    if (typeof style === 'function') {
      computedStyle = style(_objectSpread2(_objectSpread2({}, values), {}, {
        defaultStyle: defaultStyle || {}
      }));
    } else {
      computedStyle = style;
    }
    if (typeof children === 'function') {
      computedChildren = children(_objectSpread2(_objectSpread2({}, values), {}, {
        defaultChildren: defaultChildren
      }));
    } else if (children == null) {
      computedChildren = defaultChildren;
    } else {
      computedChildren = children;
    }
    return {
      className: (_computedClassName = computedClassName) !== null && _computedClassName !== void 0 ? _computedClassName : defaultClassName,
      style: computedStyle || defaultStyle ? _objectSpread2(_objectSpread2({}, defaultStyle), computedStyle) : undefined,
      children: (_computedChildren = computedChildren) !== null && _computedChildren !== void 0 ? _computedChildren : defaultChildren,
      'data-rac': ''
    };
  }, [className, style, children, defaultClassName, defaultChildren, defaultStyle, values]);
}

/**
 * A helper function that accepts a user-provided render prop value (either a static value or a function),
 * and combines it with another value to create a final result.
 */
function composeRenderProps(
// https://stackoverflow.com/questions/60898079/typescript-type-t-or-function-t-usage
value, wrap) {
  return function (renderProps) {
    return wrap(typeof value === 'function' ? value(renderProps) : value, renderProps);
  };
}
function composeTailwindRenderProps(className, tw) {
  return composeRenderProps(className, function (className) {
    return cn(tw, className);
  });
}
function useSlottedContext(context, slot) {
  var ctx = useContext(context);
  if (slot === null) {
    // An explicit `null` slot means don't use context.
    return null;
  }
  if (ctx && _typeof(ctx) === 'object' && 'slots' in ctx && ctx.slots) {
    var availableSlots = new Intl.ListFormat().format(Object.keys(ctx.slots).map(function (p) {
      return "\"".concat(p, "\"");
    }));
    if (!slot && !ctx.slots[DEFAULT_SLOT]) {
      throw new Error("A slot prop is required. Valid slot names are ".concat(availableSlots, "."));
    }
    var slotKey = slot || DEFAULT_SLOT;
    if (!ctx.slots[slotKey]) {
      // @ts-ignore
      throw new Error("Invalid slot \"".concat(slot, "\". Valid slot names are ").concat(availableSlots, "."));
    }
    return ctx.slots[slotKey];
  }
  // @ts-ignore
  return ctx;
}
function useContextProps(props, ref, context) {
  var ctx = useSlottedContext(context, props.slot) || {};
  // @ts-ignore - TS says "Type 'unique symbol' cannot be used as an index type." but not sure why.
  var _ref2 = ctx,
    contextRef = _ref2.ref,
    contextProps = _objectWithoutProperties(_ref2, _excluded);
  var mergedRef = useObjectRef(useMemo(function () {
    return mergeRefs(ref, contextRef);
  }, [ref, contextRef]));
  var mergedProps = mergeProps(contextProps, props);

  // mergeProps does not merge `style`. Adding this there might be a breaking change.
  if ('style' in contextProps && contextProps.style && 'style' in props && props.style) {
    if (typeof contextProps.style === 'function' || typeof props.style === 'function') {
      // @ts-ignore
      mergedProps.style = function (renderProps) {
        var contextStyle = typeof contextProps.style === 'function' ? contextProps.style(renderProps) : contextProps.style;
        var defaultStyle = _objectSpread2(_objectSpread2({}, renderProps.defaultStyle), contextStyle);
        var style = typeof props.style === 'function' ? props.style(_objectSpread2(_objectSpread2({}, renderProps), {}, {
          defaultStyle: defaultStyle
        })) : props.style;
        return _objectSpread2(_objectSpread2({}, defaultStyle), style);
      };
    } else {
      // @ts-ignore
      mergedProps.style = _objectSpread2(_objectSpread2({}, contextProps.style), props.style);
    }
  }
  return [mergedProps, mergedRef];
}
function useSlot() {
  // Assume we do have the slot in the initial render.
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    hasSlot = _useState2[0],
    setHasSlot = _useState2[1];
  var hasRun = useRef(false);

  // A callback ref which will run when the slotted element mounts.
  // This should happen before the useLayoutEffect below.
  var ref = useCallback(function (el) {
    hasRun.current = true;
    setHasSlot(!!el);
  }, []);

  // If the callback hasn't been called, then reset to false.
  useLayoutEffect(function () {
    if (!hasRun.current) {
      setHasSlot(false);
    }
  }, []);
  return [ref, hasSlot];
}
function useEnterAnimation(ref) {
  var isReady = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isEntering = _useState4[0],
    setEntering = _useState4[1];
  useAnimation(ref, isEntering && isReady, useCallback(function () {
    return setEntering(false);
  }, []));
  return isEntering && isReady;
}
function useExitAnimation(ref, isOpen) {
  // State to trigger a re-render after animation is complete, which causes the element to be removed from the DOM.
  // Ref to track the state we're in, so we don't immediately reset isExiting to true after the animation.
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isExiting = _useState6[0],
    setExiting = _useState6[1];
  var _useState7 = useState('idle'),
    _useState8 = _slicedToArray(_useState7, 2),
    exitState = _useState8[0],
    setExitState = _useState8[1];
  var _isExiting = isExiting;
  // If isOpen becomes false, set isExiting to true.
  if (!isOpen && ref.current && exitState === 'idle') {
    _isExiting = true;
    setExiting(true);
    setExitState('exiting');
  }

  // If we exited, and the element has been removed, reset exit state to idle.
  if (!ref.current && exitState === 'exited') {
    setExitState('idle');
  }
  useAnimation(ref, _isExiting, useCallback(function () {
    setExitState('exited');
    setExiting(false);
  }, []));
  return _isExiting;
}
function useAnimation(ref, isActive, onEnd) {
  var prevAnimation = useRef(null);
  if (isActive && ref.current) {
    // This is ok because we only read it in the layout effect below, immediately after the commit phase.
    // We could move this to another effect that runs every render, but this would be unnecessarily slow.
    // We only need the computed style right before the animation becomes active.
    prevAnimation.current = window.getComputedStyle(ref.current).animation;
  }
  useLayoutEffect(function () {
    if (isActive && ref.current) {
      // Make sure there's actually an animation, and it wasn't there before we triggered the update.
      var computedStyle = window.getComputedStyle(ref.current);
      if (computedStyle.animationName && computedStyle.animationName !== 'none' && computedStyle.animation !== prevAnimation.current) {
        var onAnimationEnd = function onAnimationEnd(e) {
          if (e.target === ref.current) {
            element.removeEventListener('animationend', onAnimationEnd);
            ReactDOM.flushSync(function () {
              onEnd();
            });
          }
        };
        var element = ref.current;
        element.addEventListener('animationend', onAnimationEnd);
        return function () {
          element.removeEventListener('animationend', onAnimationEnd);
        };
      } else {
        onEnd();
      }
    }
    return;
  }, [ref, isActive, onEnd]);
}

// React doesn't understand the <template> element, which doesn't have children like a normal element.
// It will throw an error during hydration when it expects the firstChild to contain content rendered
// on the server, when in reality, the browser will have placed this inside the `content` document fragment.
// This monkey patches the firstChild property for our special hidden template elements to work around this error.
// See https://github.com/facebook/react/issues/19932
if (typeof HTMLTemplateElement !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  var getFirstChild = Object.getOwnPropertyDescriptor(Node.prototype, 'firstChild').get;
  Object.defineProperty(HTMLTemplateElement.prototype, 'firstChild', {
    configurable: true,
    enumerable: true,
    get: function get() {
      if (this.dataset.reactAriaHidden) {
        return this.content.firstChild;
      } else {
        return getFirstChild.call(this);
      }
    }
  });
}
var HiddenContext = /*#__PURE__*/createContext(false);

// Portal to nowhere
var hiddenFragment = typeof DocumentFragment !== 'undefined' ? new DocumentFragment() : null;
function Hidden(props) {
  var isHidden = useContext(HiddenContext);
  var isSSR = useIsSSR();
  if (isHidden) {
    // Don't hide again if we are already hidden.
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, props.children);
  }
  var children = /*#__PURE__*/React__default.createElement(HiddenContext.Provider, {
    value: true
  }, props.children);

  // In SSR, portals are not supported by React. Instead, render into a <template>
  // element, which the browser will never display to the user. In addition, the
  // content is not part of the DOM tree, so it won't affect ids or other accessibility attributes.
  return isSSR ? /*#__PURE__*/React__default.createElement("template", {
    "data-react-aria-hidden": true
  }, children) : (
  /*#__PURE__*/
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  ReactDOM.createPortal(children, hiddenFragment));
}

/**
 * Filters out `data-*` attributes to keep them from being passed down and duplicated.
 * @param props
 */
function removeDataAttributes(props) {
  var prefix = /^(data-.*)$/;
  var filteredProps = {};
  for (var prop in props) {
    if (!prefix.test(prop)) {
      filteredProps[prop] = props[prop];
    }
  }
  return filteredProps;
}

// Override base type to change the default.

export { DEFAULT_SLOT, Hidden, HiddenContext, Provider, composeRenderProps, composeTailwindRenderProps, removeDataAttributes, useContextProps, useEnterAnimation, useExitAnimation, useRenderProps, useSlot, useSlottedContext };
//# sourceMappingURL=index.esm.js.map
